/* eslint-disable max-statements */

import { Button, Group, Image, LoadingOverlay, Tabs, useMantineTheme } from '@mantine/core';
import { useOverrideHooks } from '@override';
import { IconCircleFilled } from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import {
  Batch,
  BatchCustomsStateNotificationType,
  BatchStatusEnum,
  BatchTypeEnum,
  EnvironmentCountries,
  IBatchesCriteria,
  TemplateType,
} from '@e-origin/shared';

import { IconFactory } from '@assets';
import { ListFilterChangeEvent, ListFilterDropdown, ListFilterDropdownElements, SearchBox, Table } from '@components';
import { BATCH_LIST_NEXT_PAGE } from '@constants';
import { BatchesTabs } from '@enums';
import { ContentLayout } from '@features/layout';
import { getInitialCriteria, prefetchBatches, useBatches } from '@hooks/batch';
import { useCustomersNamesAndIds } from '@hooks/customer';
import { useDeclarant } from '@hooks/declarant';
import { useSettings } from '@hooks/useSettings';
import { useTemplates } from '@hooks/useTemplates';
import { buildDropdownOptions, removeUndefined, STORAGE_KEYS } from '@utils';
import { getStoredCriteria, setStoredCriteria } from '../../criteria.caching';
import { useBuildActionHandlers, useBuildArchivedColumns, useBuildDefaultColumnVisibility } from './BatchListColumns';
import { useBatchModalManager } from './batchModalManager';
import { BatchView } from './BatchView';
import { UserVisibility } from './UserVisibility';

export const BatchList = () => {
  const theme = useMantineTheme();
  const OverrideHooks = useOverrideHooks();

  const batchModalManager = useBatchModalManager();
  const { data: settings } = useSettings();

  const batchViewCookies = getStoredCriteria(STORAGE_KEYS.COOKIES.BATCH_VIEW) || '';

  const criteria = getInitialCriteria();

  const storeCriteria = () => {
    setStoredCriteria(STORAGE_KEYS.COOKIES.LIST_BATCHES_CRITERIA, { filters, sorting, pagination });
  };

  const [filters, setFilters] = useState<IBatchesCriteria['filters']>(criteria.filters);
  const [sorting, setSorting] = useState<IBatchesCriteria['sorting']>(criteria.sorting);
  const [pagination, setPagination] = useState<IBatchesCriteria['pagination']>(criteria.pagination);

  const navigate = useNavigate();
  const { tabId, customerId } = useParams();

  const [batchesData, setBatchesData] = useState<Batch[]>([]);
  const [batchView, setBatchView] = useState(batchViewCookies as BatchTypeEnum);
  const selectedTabRef = useRef('');

  const { data: { list: batchesList, total: totalBatchCount } = { list: [], total: 0 }, isLoading } = useBatches({ filters, sorting, pagination });
  const { data: templatesList } = useTemplates();
  const customersNamesAndIds = useCustomersNamesAndIds();
  const { data: declarant } = useDeclarant();

  const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([]);

  const handleSelectBatch = (id: string, checked: boolean) => {
    setSelectedBatchIds((prev) => (checked ? [...prev, id] : prev.filter((batchId) => batchId !== id)));
  };

  const handleSelectAllBatches = (checked: boolean, allIds: string[]) => {
    setSelectedBatchIds(checked ? allIds : []);
  };

  const filterTemplates = useMemo(() => {
    return (
      templatesList
        ?.filter((t) => t.type === filters.templateType && t.published !== false && (filters.isExport === true ? t.isExport === true : t.isExport !== true))
        .map((template) => ({
          label: template.name,
          value: template._id,
        })) || []
    );
  }, [templatesList, filters.templateType, filters.isExport]);

  const tabs = useRef<{ id: BatchesTabs; label: string }[]>([
    { id: BatchesTabs.IN_PROGRESS, label: 'Current' },
    { id: BatchesTabs.ARCHIVED, label: 'Archived' },
  ]);

  useEffect(() => {
    if (!tabId && tabs.current.length > 0) {
      navigate(`/batches/${tabs.current[0].id}`, { replace: true });
    }
  }, [tabId, tabs, navigate]);

  const currentTab = tabs.current.find((tab) => tab.id === tabId)?.id;

  if (tabId && !currentTab && tabs.current.length > 0) {
    return <Navigate to={`/batches/${tabs.current[0].id}`} replace />;
  }

  const setBatchesFilters = (updatedFilters: Partial<IBatchesCriteria['filters']>) => {
    setFilters((prevFilters) => removeUndefined({ ...prevFilters, ...updatedFilters }));
  };

  useEffect(() => {
    storeCriteria();
  }, [pagination, sorting, filters]);

  useEffect(() => {
    if (templatesList?.length) {
      setBatchesFilters({
        isExport: batchView === BatchTypeEnum.HIGH_VALUES_EXPORT || undefined,
        status: tabId === BatchesTabs.ARCHIVED ? BatchStatusEnum.ARCHIVED : undefined,
        templateType: [BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(batchView)
          ? TemplateType.HIGH_VALUE_H1
          : TemplateType.LOW_VALUE_H7,
      });
    }
  }, [templatesList]);

  useEffect(() => {
    if (!batchesList.length && !batchesData.length) {
      return;
    }
    if (customerId && batchesList.length) {
      setBatchesData(batchesList.filter((batch) => batch.customer === customerId));
    } else {
      setBatchesData(batchesList);
    }
  }, [batchesList]);

  const prefetchNextPage = (page: number) => prefetchBatches({ filters, sorting, pagination: { ...pagination, page } });

  const [columnsDropdownOptions, setColumnsDropdownOptions] = useState<ListFilterDropdownElements>(() => useBuildDefaultColumnVisibility(settings));

  useEffect(() => {
    if (settings) {
      setColumnsDropdownOptions(useBuildDefaultColumnVisibility(settings));
    }
  }, [settings?.isForwarder]);

  const updateColumnsVisibility = (columnOption: ListFilterChangeEvent) => {
    const columnsDropdownOptionsCopy = { ...columnsDropdownOptions };

    if (!columnOption.id) {
      Object.keys(columnsDropdownOptionsCopy).forEach((key) => {
        columnsDropdownOptionsCopy[key].checked = columnOption.checked;
      });
    } else {
      columnsDropdownOptionsCopy[columnOption.id].checked = columnOption.checked;
    }

    localStorage.setItem(
      STORAGE_KEYS.LOCAL_STORAGE.LIST_BATCHES_COLUMNS,
      Object.keys(columnsDropdownOptionsCopy)
        .filter((key) => columnsDropdownOptionsCopy[key].checked)
        .join(','),
    );

    setColumnsDropdownOptions(columnsDropdownOptionsCopy);
  };

  const filterByCustomersHandler = ({ id, checked }) => {
    let filterByCustomers;
    if (!id) {
      filterByCustomers = checked ? customersNamesAndIds.map((c) => c.value) : [];
    } else {
      filterByCustomers = customersNamesAndIds.reduce((acc, cur) => {
        if ((id === cur.value && checked) || (filters.filterByCustomers?.includes(cur.value) && id !== cur.value)) {
          acc.push(cur.value);
        }
        return acc;
      }, []);
    }
    setBatchesFilters({ ...filters, filterByCustomers });
  };

  const filterByTemplatesHandler = ({ id, checked }) => {
    let filterByTemplates;
    if (!id) {
      filterByTemplates = checked ? filterTemplates.map((t) => t.value) : [];
    } else {
      filterByTemplates = filterTemplates.reduce((acc, cur) => {
        if ((id === cur.value && checked) || (filters.filterByTemplates?.includes(cur.value) && id !== cur.value)) {
          acc.push(cur.value);
        }
        return acc;
      }, []);
    }
    setBatchesFilters({ ...filters, filterByTemplates });
  };

  const buildNotificationsDropdownOptions = () => {
    const notificationsDropdownOptionList: ListFilterDropdownElements = {
      ...(settings?.country === EnvironmentCountries.NL && {
        [BatchCustomsStateNotificationType.ACTIONS_PENDING]: {
          label: 'Actions pending',
          checked: filters.filterByNotifications?.includes(BatchCustomsStateNotificationType.ACTIONS_PENDING) ?? false,
          icon: <IconCircleFilled size={20} color={theme.colors.red[8]} />,
        },
        [BatchCustomsStateNotificationType.NOTIFICATIONS_PENDING]: {
          label: 'Notifications pending',
          checked: filters.filterByNotifications?.includes(BatchCustomsStateNotificationType.NOTIFICATIONS_PENDING) ?? false,
          icon: <IconCircleFilled size={20} color={theme.colors.yellow[5]} />,
        },
      }),
      [BatchCustomsStateNotificationType.COMMENTS]: {
        label: 'Comments',
        checked: filters.filterByNotifications?.includes(BatchCustomsStateNotificationType.COMMENTS) ?? false,
        icon: <Image src={IconFactory.comment} alt="Comments" w={20} h={20} />,
      },
    };

    return notificationsDropdownOptionList;
  };
  const [notificationsDropdownOptions, setNotificationsDropdownOptions] = useState(buildNotificationsDropdownOptions());

  const loadBatches = (batchViewType?: BatchTypeEnum) => {
    if (templatesList?.length) {
      const tableView = batchViewType ?? batchView;
      let batchType: TemplateType;
      if ([BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(tableView)) {
        batchType = TemplateType.HIGH_VALUE_H1;
      }
      if (tableView === BatchTypeEnum.LOW_VALUES) {
        batchType = TemplateType.LOW_VALUE_H7;
      }

      let status: BatchStatusEnum;
      if (tabId) {
        if (tabId === BatchesTabs.ARCHIVED) {
          status = BatchStatusEnum.ARCHIVED;
        }
      } else if (selectedTabRef.current === BatchesTabs.ARCHIVED) {
        status = BatchStatusEnum.ARCHIVED;
      }

      setBatchesFilters({
        isExport: tableView === BatchTypeEnum.HIGH_VALUES_EXPORT,
        templateType: batchType,
        status,
      });
    }
  };

  const searchBatches = (searchQuery: string) => {
    setBatchesFilters({ searchQuery });
    setPagination({ page: 1, size: pagination.size });
    prefetchNextPage(BATCH_LIST_NEXT_PAGE);
  };

  const { handleTableAction, handleArchivedTableAction } = useBuildActionHandlers({ loadBatches, batchView });

  const archivedColumns = useBuildArchivedColumns({ handleArchivedTableAction, batchView, currentTab: tabId });

  const columns = OverrideHooks.useBuildColumns({
    columnsDropdownOptions,
    handleTableAction,
    declarant,
    settings,
    batchView,
    selectedBatchIds,
    handleSelectBatch,
    handleSelectAllBatches,
    allBatchIds: batchesData.map((b) => b._id),
    batchesData,
  });

  const filterByNotificationsHandler = (notificationOption) => {
    const { id, checked } = notificationOption;

    if (!id) return;

    const current = filters.filterByNotifications || [];

    const updated = checked ? [...current, id] : current.filter((item) => item !== id);

    setBatchesFilters({
      ...filters,
      filterByNotifications: updated,
    });
  };

  const afterCreateBatchModal = () => {
    return loadBatches();
  };

  const onTabChangeHandler = (tab: BatchesTabs) => {
    navigate(`/batches/${tab}`, { replace: true });
    setBatchesFilters({
      status: tab === BatchesTabs.ARCHIVED ? BatchStatusEnum.ARCHIVED : undefined,
    });
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
    prefetchNextPage(page + 1);
  };

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination({ page: 1, size: pagination.size });
    prefetchNextPage(BATCH_LIST_NEXT_PAGE);
  };

  if (!tabId) {
    return <LoadingOverlay visible overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Shipment batches</ContentLayout.Title>
        <ContentLayout.Filters>
          <BatchView
            value={batchView}
            onChange={(item) => {
              setPagination({ page: 1, size: pagination.size });
              setBatchView(item);
              setBatchesFilters({ filterByTemplates: [], filterByCustomers: [], filterByNotifications: [] });
              loadBatches(item);
            }}
          />
          <Button
            onClick={() => batchModalManager.openNewBatch({ batchType: batchView, afterCreate: afterCreateBatchModal })}
            data-pw="new-batch-btn"
            variant="outline"
            size="md"
            h={55}
          >
            New batch
          </Button>
          <SearchBox handleSearch={searchBatches} value={filters?.searchQuery} placeholder="Search by counter, name or file name" data-pw="search-input" />
        </ContentLayout.Filters>
      </ContentLayout.Header>
      <ContentLayout.SubHeader>
        <Group justify="space-between" align="center">
          <Tabs value={tabId} onChange={(value) => onTabChangeHandler(value as BatchesTabs)}>
            <Tabs.List>
              {tabs.current.map((tab) => (
                <Tabs.Tab key={tab.id} value={tab.id}>
                  {tab.label}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
          <Group>
            {[BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(batchView) && (
              <ListFilterDropdown title={'Notifications'} values={notificationsDropdownOptions} onChange={filterByNotificationsHandler} hideShowAll />
            )}
            <ListFilterDropdown
              title="Customers"
              values={buildDropdownOptions(customersNamesAndIds, filters.filterByCustomers)}
              onChange={filterByCustomersHandler}
              hideShowAll={false}
            />
            <ListFilterDropdown
              title="Templates"
              values={buildDropdownOptions(filterTemplates, filters.filterByTemplates)}
              onChange={filterByTemplatesHandler}
              hideShowAll={false}
            />
            <UserVisibility declarant={declarant} userFilters={filters.filterByUsers} onChange={(users) => setBatchesFilters({ filterByUsers: users })} />
            <ListFilterDropdown title={'Columns visibility'} values={columnsDropdownOptions} onChange={updateColumnsVisibility} data-pw="columns-visibility" />
          </Group>
        </Group>
      </ContentLayout.SubHeader>
      <ContentLayout.Content>
        <Table
          columns={tabId === BatchesTabs.IN_PROGRESS ? columns : archivedColumns}
          dataSource={batchesData ?? []}
          total={totalBatchCount}
          onPaginationChange={handlePagination}
          onSortingChange={handleSorting}
          isLoading={isLoading}
          defaultPage={pagination.page}
          defaultPerPage={pagination.size}
          defaultSorting={sorting.field}
          defaultSortingDirection={sorting.direction}
        />
      </ContentLayout.Content>
    </ContentLayout>
  );
};
